import { createContext, useContext, useSyncExternalStore } from 'react';
import { EMPTY_DIALOG, getDialog, subscribeDialog } from './dialogStore.js';
import type { DialogData, DialogStore } from './dialogStore.js';
import type { ReactNode } from 'react';

// NOTE!
// - DO NOT use the `DialogContext` outside of this file, except for tests. It is only exported for tests.
export const DialogContext = createContext<DialogData>(EMPTY_DIALOG);

interface DialogProviderProps {
  dialogStore?: DialogStore;
  children?: ReactNode;
}

/**
 * - Client-side: omit `dialogStore` — uses the default module-level singleton (safe: one user per browser tab).
 * - SSR: pass a fresh `createDialogStore()` instance per request to avoid cross-request leakage. The server
 *   snapshot is always `EMPTY_DIALOG`, so a dialog is never part of server-rendered HTML.
 */
export const DialogProvider = ({ dialogStore, children }: DialogProviderProps) => {
  const subscribe = dialogStore ? dialogStore.subscribe : subscribeDialog;
  const getSnapshot = dialogStore ? dialogStore.getDialog : getDialog;
  const data = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_DIALOG);
  return <DialogContext.Provider value={data}>{children}</DialogContext.Provider>;
};

export const useDialog = (): DialogData => useContext(DialogContext);
